import { UserJobType, UserLevelType } from "@redotech/account-common/job-level";
import { Json } from "@redotech/json/json";
import { BillingSettings } from "@redotech/redo-billing-common/team-billing";
import { AiFileAnalysis } from "@redotech/redo-common-model/file/uploaded-file";
import { AdvancedFlow } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  ConversationFiltersV3,
  CONVERSATIONS_FILTERS_VERSION,
} from "@redotech/redo-model/conversation-filters/conversation-filters";
import { ImportProvider } from "@redotech/redo-model/customer-import";
import {
  GetConversationCountsBody,
  serializeGetConversationCountsBody,
} from "@redotech/redo-model/get-conversation-counts-body";
import { FlowType } from "@redotech/redo-model/return-flow";
import { ShopifyIntegration } from "@redotech/redo-model/shopify";
import { GetTeam, Team } from "@redotech/redo-model/team";
import { Coverage } from "@redotech/redo-return-coverage-common/coverage";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { isDef } from "@redotech/util/checks";
import { retry } from "@redotech/util/retry";
import { RedoMerchantClient } from ".";

export interface TeamBillingInfo {
  lastBill: { amount: number; date: string; status: string } | null;
  nextBill: { amount: number; date: string };
}

/**
 * POST /team/upload
 */
export async function createUpload(
  client: RedoMerchantClient,
  {
    file,
    maxFileSizeKb,
    maxImageWidthPx,
    recommendedLongestSidePx,
    autoOptimizeImage,
    preserveGifs,
    signal,
  }: {
    file: File;
    maxFileSizeKb?: number;
    maxImageWidthPx?: number;
    recommendedLongestSidePx?: number;
    autoOptimizeImage?: boolean;
    preserveGifs?: boolean;
    signal?: AbortSignal;
  },
): Promise<{ url: string }> {
  const data = new FormData();
  data.append("image", file);
  if (isDef(maxFileSizeKb)) {
    data.append("maxFileSizeKb", maxFileSizeKb.toString());
  }
  if (isDef(maxImageWidthPx)) {
    data.append("maxImageWidthPx", maxImageWidthPx.toString());
  }
  if (isDef(recommendedLongestSidePx)) {
    data.append(
      "recommendedLongestSidePx",
      recommendedLongestSidePx.toString(),
    );
  }
  if (isDef(autoOptimizeImage)) {
    data.append("autoOptimizeImage", autoOptimizeImage.toString());
  }
  if (isDef(preserveGifs)) {
    data.append("preserveGifs", preserveGifs.toString());
  }
  const response = await client.client.postForm("team/upload", data, {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * POST /team/upload-document
 * Special upload endpoint for HTML and EML files that processes them before storage
 */
export async function createDocumentUpload(
  client: RedoMerchantClient,
  { file, signal }: { file: File; signal?: AbortSignal },
): Promise<{ url: string; analysis: AiFileAnalysis }> {
  const data = new FormData();
  data.append("document", file);
  const response = await client.client.postForm("team/upload-document", data, {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * POST /team/upload-attachment
 * Upload endpoint for file attachments (PDF, DOC, etc.) without image processing
 */
export async function createAttachmentUpload(
  client: RedoMerchantClient,
  {
    file,
    maxFileSizeKb,
    signal,
  }: { file: File; maxFileSizeKb?: number; signal?: AbortSignal },
): Promise<{ url: string; fileSize: number; fileName: string }> {
  const data = new FormData();
  data.append("attachment", file);
  if (isDef(maxFileSizeKb)) {
    data.append("maxFileSizeKb", maxFileSizeKb.toString());
  }
  const response = await client.client.postForm(
    "team/upload-attachment",
    data,
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * GET /team
 */
export async function getCurrentTeam(
  client: RedoMerchantClient,
  { signal }: { signal?: AbortSignal },
): Promise<GetTeam> {
  const response = await client.client.get("team", {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * GET /team/:teamId/url
 */
export async function getTeam(
  client: RedoMerchantClient,
  { teamId, signal }: { teamId: string; signal?: AbortSignal },
): Promise<string> {
  const response = await client.client.get(`team/${teamId}/url`, { signal });
  return response.data;
}

/**
 * GET /team_urls
 */
export async function getTeamUrls(
  client: RedoMerchantClient,
  { ids, signal }: { ids: string[]; signal?: AbortSignal },
): Promise<{ id: string; url: string; logoUrl: string; name: string }[]> {
  const params = ids.map((id) => `ids=${id}`).join("&");
  const response = await client.client.get(`team/urls?${params}`, { signal });
  return response.data;
}

/**
 * GET /team/shopify-store
 */
export async function getShopifyStore(
  client: RedoMerchantClient,
  { signal }: { signal?: AbortSignal },
  teamID: string,
): Promise<string> {
  const response = await client.client.get(
    `team/shopify-store/${encodeURIComponent(teamID)}`,
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * GET /teams/:teamId/billing-date
 */
export async function getBillingInfo(
  client: RedoMerchantClient,
  {
    signal,
    teamId,
    settings,
  }: { signal?: AbortSignal; teamId: string; settings: BillingSettings },
): Promise<TeamBillingInfo> {
  const response = await client.client.post(
    `team/${encodeURIComponent(teamId)}/billing-info`,
    { settings },
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * POST /team
 * @deprecated use a more specific endpoint instead that only allows modifying
 * the settings you need to modify. This endpoint allows a merchant to change
 * any of their settings, including pricing and access to features.
 */
export async function updateCurrentTeam(
  client: RedoMerchantClient,
  {
    shopify,
    team,
    signal,
  }: { shopify?: ShopifyIntegration; team: Team; signal?: AbortSignal },
) {
  const response = await client.client.post(
    "team",
    { shopify, team },
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * POST /team/email-exclusions
 */
export async function updateEmailExclusions(
  client: RedoMerchantClient,
  { exclusionList, signal }: { exclusionList: string[]; signal?: AbortSignal },
) {
  const response = await client.client.post(
    "team/email-exclusions",
    { exclusionList },
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * POST /team/coverage
 */
export async function updateCoverage(
  client: RedoMerchantClient,
  {
    coverageEnabled,
    coverageExcludeCollections,
    coverageExcludeTags,
    coverageExcludeProductTags,
    coverageExcludeSources,
    coverage,
    signal,
  }: {
    coverageExcludeCollections: readonly string[];
    coverageExcludeTags: readonly string[];
    coverageExcludeProductTags: readonly string[];
    coverageExcludeSources: readonly string[];
    coverageEnabled: boolean;
    coverage: Coverage;
    signal?: AbortSignal;
  },
) {
  const response = await client.client.post(
    "team/coverage",
    {
      coverageEnabled,
      coverageExcludeCollections,
      coverageExcludeTags,
      coverageExcludeProductTags,
      coverageExcludeSources,
      coverage,
    },
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * DELETE /team/user/:user_id
 */
export async function deleteTeamUser(
  client: RedoMerchantClient,
  { user, signal }: { user: { _id: string }; signal?: AbortSignal },
) {
  const response = await client.client.delete(`team/user/${user._id}`, {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * POST /team/user
 */
export async function createTeamUser(
  client: RedoMerchantClient,
  {
    email,
    firstName,
    lastName,
    roles,
    sendEmail,
    customNote,
    jobProfile,
    signal,
  }: {
    email: string;
    firstName: string;
    lastName: string;
    roles: string[];
    sendEmail: boolean;
    customNote: string | undefined;
    jobProfile?: { job: UserJobType[]; level: UserLevelType | null };
    signal?: AbortSignal;
  },
) {
  const response = await client.client.post(
    "team/user",
    { email, firstName, lastName, roles, sendEmail, customNote, jobProfile },
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * POST /team/tags
 */
export async function addTags(
  client: RedoMerchantClient,
  { tags, signal }: { tags: { name: string }[]; signal?: AbortSignal },
) {
  const response = await client.client.post(
    "team/tags",
    { tags },
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * PUT /team/tags
 */
export async function updateTags(
  client: RedoMerchantClient,
  {
    updates,
    signal,
  }: {
    updates: { oldName: string; newName: string; pillTheme: PillTheme }[];
    signal?: AbortSignal;
  },
) {
  const response = await client.client.put(
    "team/tags",
    { updates },
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * GET /team/returns
 */
export async function getTeamReturns(
  client: RedoMerchantClient,
  {
    email,
    limit,
    signal,
  }: { email: string; limit?: number; signal?: AbortSignal },
) {
  const params = new URLSearchParams({ email });
  if (limit !== undefined) {
    params.set("limit", String(limit));
  }
  const response = await client.client.get(
    `team/returns?${params.toString()}`,
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * GET /team/orders
 */
export async function getTeamOrders(
  client: RedoMerchantClient,
  { email, signal }: { email: string; signal?: AbortSignal },
) {
  const response = await client.client.get(
    `team/orders?email=${encodeURIComponent(email)}`,
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * DELETE /team/email-flow/:id
 */
export async function deleteEmailFlow(
  client: RedoMerchantClient,
  { id, signal }: { id: string; signal?: AbortSignal },
) {
  const response = await client.client.delete(`team/email-flow/${id}`, {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * PUT /team/advanced-flow/:id
 */
export async function saveAdvancedFlow(
  client: RedoMerchantClient,
  steps: AdvancedFlow["steps"],
  flowId: string,
  name: string,
  { signal }: { signal?: AbortSignal },
): Promise<{ flow: AdvancedFlow }> {
  const response = await client.client.put(
    `team/advanced-flow/${flowId}`,
    { steps, name },
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * GET /team/flows/:flowType
 */
export async function getCurrentFlow(
  client: RedoMerchantClient,
  flowType: FlowType,
  requirePublished: boolean,
  { signal }: { signal?: AbortSignal },
): Promise<{ flow: Json }> {
  const response = await client.client.get(`team/flows/${flowType}`, {
    headers: client.authorization(),
    signal,
    params: { requirePublished },
  });
  return response.data;
}

/**
 * GET /team/flows/:flowType
 */
export async function getFlowByID(
  client: RedoMerchantClient,
  flowType: FlowType,
  flowId: string,
  { signal }: { signal?: AbortSignal },
): Promise<{ flow: Json }> {
  const response = await client.client.get(`team/flows/${flowType}/${flowId}`, {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * GET /team/flow-versions/:flowType
 */
export async function getFlowVersions(
  client: RedoMerchantClient,
  flowType: FlowType,
  { signal }: { signal?: AbortSignal },
): Promise<{ flows: Json[] }> {
  const response = await client.client.get(`team/flows-versions/${flowType}`, {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * POST /team/flows/:flowType
 */
export async function saveFlow(
  client: RedoMerchantClient,
  flowType: FlowType,
  flow: Json,
  { signal }: { signal?: AbortSignal },
): Promise<{ flow: Json }> {
  const response = await client.client.post(`team/flows/${flowType}`, flow, {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * POST /team/flows/:flowType/publish
 */
export async function publishFlow(
  client: RedoMerchantClient,
  flowType: FlowType,
  name: string,
  { signal }: { signal?: AbortSignal },
): Promise<{ flow: Json }> {
  const response = await client.client.post(
    `team/flows/${flowType}/publish`,
    { name },
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * POST /team/flows/:flowType/revert/:id
 */
export async function revertFlow(
  client: RedoMerchantClient,
  flowType: FlowType,
  flowId: string,
  { signal }: { signal?: AbortSignal },
): Promise<{ flow: Json }> {
  const response = await client.client.post(
    `team/flows/${flowType}/revert/${flowId}`,
    {},
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * GET team/conversations/counts
 */
export async function getConversationsCounts(
  client: RedoMerchantClient,
  {
    views,
    signal,
    presetViewFilters,
    includeAI,
  }: {
    views?: { name: string; filters: ConversationFiltersV3 }[];
    signal?: AbortSignal;
    presetViewFilters?: Record<string, ConversationFiltersV3>;
    includeAI?: boolean;
  },
): Promise<Record<string, number>> {
  return retry(
    async () => {
      const v2Views = (views || []).map((view) => {
        return { viewName: view.name, filters: view.filters };
      });

      const presetV2Filters =
        Object.entries(presetViewFilters || {}).map(([viewName, filters]) => {
          return { viewName, filters };
        }) || [];

      const body: GetConversationCountsBody = {
        views: [...presetV2Filters, ...v2Views],
      };
      const serializedBody = serializeGetConversationCountsBody(body);

      const url = "team/conversations/counts";

      const v3Header = { "x-api-version": CONVERSATIONS_FILTERS_VERSION };

      const includeAIParam = includeAI === true ? "true" : "false";
      const urlWithParams = `${url}?includeAI=${includeAIParam}`;

      const response = await client.client.post(urlWithParams, serializedBody, {
        headers: { ...client.authorization(), ...v3Header },
        signal,
      });

      return response.data;
    },
    4,
    (count) =>
      Temporal.Duration.from({ milliseconds: [500, 1000, 3000][count - 1] }),
  );
}

/**
 * GET team/conversations/has-any-open
 */
export async function checkHasAnyOpenConversations(
  client: RedoMerchantClient,
  { signal }: { signal?: AbortSignal },
): Promise<{ hasAny: boolean }> {
  const response = await client.client.get("team/conversations/has-any-open", {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * GET team/multipass-availability
 */
export async function getMultipassAvailability(
  client: RedoMerchantClient,
  { signal }: { signal?: AbortSignal },
): Promise<{ available: boolean }> {
  const response = await client.client.get("team/multipass-availability", {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * POST /team/customer-imports
 */
export async function createCustomerImport(
  client: RedoMerchantClient,
  {
    file,
    provider,
    checkUnsubscribed,
    customFields,
    signal,
    ...segment
  }: {
    file: File;
    provider: ImportProvider;
    checkUnsubscribed: boolean;
    customFields?: Record<string, string>;
    signal?: AbortSignal;
  } & (
    | { segmentName: string; segmentId?: undefined }
    | { segmentId: string; segmentName?: undefined }
  ),
): Promise<{ workflowId: string }> {
  const data = new FormData();
  data.append("file", file);
  data.append("provider", provider);
  if (segment.segmentId) {
    data.append("segmentId", segment.segmentId);
  } else if (segment.segmentName) {
    data.append("segmentName", segment.segmentName);
  }
  data.append("checkUnsubscribed", checkUnsubscribed.toString());
  if (customFields && Object.keys(customFields).length > 0) {
    data.append("customFields", JSON.stringify(customFields));
  }

  const response = await client.client.postForm("team/customer-imports", data, {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * GET /team/customer-imports
 */
export async function listCustomerImports(
  client: RedoMerchantClient,
  signal?: AbortSignal,
): Promise<{ imports: { importId: string }[] }> {
  const response = await client.client.get("team/customer-imports", {
    headers: client.authorization(),
    signal,
  });
  return response.data;
}

/**
 * POST /team/customer-imports/:workflowId/cancel
 */
export async function cancelCustomerImport(
  client: RedoMerchantClient,
  { workflowId, signal }: { workflowId: string; signal?: AbortSignal },
): Promise<{ success: boolean }> {
  const response = await client.client.post(
    `team/customer-imports/${workflowId}/cancel`,
    {},
    { headers: client.authorization(), signal },
  );
  return response.data;
}
